import clsx from 'clsx'
import GridSpinner from 'components/UI/GridSpinner'
import { Grid, NetworkIcon, Typography } from 'gu-ui-kit'
import { DoublePanelViewLayout } from 'layouts'
import formsMap from 'modules/Forms/Account/Create'
import formStyles from 'modules/Forms/common-form.module.scss'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useHistory, useRouteMatch } from 'react-router'
import validator from 'utils/asyncValidator'
import useNetworkPalette from 'utils/hooks/useNetworkPalette'
import AsideDecoration from 'modules/Services/AsideDecoration'
import styles from 'modules/Services/AccountCreate/account-register.module.scss'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PropTypes from 'prop-types'
import SERVICES_API_V1 from 'api/services/v1'
import {
  ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
  SET_NETWORK_DATA,
} from 'modules/Services/types'
import HighRiskLabel from 'modules/Services/Network/subcomponents/HighRiskLabel'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'

const AccountCreate = ({ multiLogins = false }) => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const {
    params: { i18n, networkType },
  } = useRouteMatch()

  const { networkId } = useNetworksData(networkType)

  const { connectManualLoading, manual, bulk_account_creation_allowed } =
    useSelector((state) => ({
      connectManualLoading:
        state['services/account-create'].connectManualLoading,
      manual: state['services/account-create'].connectManualText,
      bulk_account_creation_allowed:
        state['services/network'].bulk_account_creation_allowed,
    }))

  const formConfig = useMemo(() => formsMap?.[networkType], [networkType])
  const paletteClass = useNetworkPalette(networkType)

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (multiLogins) {
      SERVICES_API_V1.getNetworkSettings({ networkId }).then(
        ({ response: { data } }) => {
          if (!data?.bulk_account_creation_allowed) {
            return history.push(
              pathGen(ROUTES.SERVICES_NETWORK, {
                networkType,
              }),
            )
          }

          dispatch({
            type: SET_NETWORK_DATA,
            payload: {
              bulk_account_creation_allowed:
                !!data?.bulk_account_creation_allowed,
            },
          })
        },
      )
    }

    dispatch({
      type: ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
      payload: {
        networkId,
        params: { lang: i18n },
      },
    })
  }, [networkId])

  if (!formConfig) {
    return <Redirect to={pathGen(ROUTES.SERVICES)} />
  }

  const FormComponent = useMemo(() => formConfig.component, [formConfig])

  const SideContent = useMemo(() => {
    if (connectManualLoading) {
      return (
        <div className={styles['aside-decoration__container']}>
          <GridSpinner width={40} />
        </div>
      )
    }

    return !manual.text ? (
      <AsideDecoration
        additionalText={
          networkType.match(/high-risk$/) ? <HighRiskLabel /> : null
        }
        icon={
          <Grid fluid={false} direction={'row'} gap={16} alignItems={'center'}>
            <NetworkIcon
              dark={paletteClass === 'light'}
              rounded
              width={56}
              name={networkType}
              type={'unlinked'}
            />
            <Typography
              component={'div'}
              variant={'p2'}
              weight={'medium'}
              className={styles['palette_' + paletteClass]}
            >
              {t(`services::networks.${networkType}`)}
            </Typography>
          </Grid>
        }
        backgroundColor={`var(--network-color-${networkType})`}
        className={clsx(
          styles['aside-decoration__container'],
          styles['palette_' + paletteClass],
        )}
      />
    ) : (
      <div className={styles['aside__manual-container']}>
        <Typography variant={'h6'}>{manual.header}</Typography>
        <Typography
          variant={'p7'}
          component={'div'}
          dangerouslySetInnerHTML={{
            __html: manual.text,
          }}
        />
      </div>
    )
  }, [manual, connectManualLoading])

  return (
    <>
      <Helmet defer={false}>
        <title>
          {t('common::nav_menu.network_account_register')}{' '}
          {t(`services::networks.${networkType}`)}
        </title>
      </Helmet>

      <DoublePanelViewLayout
        contentClassName={styles['content-side-container']}
        side={SideContent}
        onClose={(e) => {
          e.preventDefault()
          history.push(
            pathGen(ROUTES.SERVICES_NETWORK, {
              i18n,
              networkType,
            }),
          )
        }}
      >
        <div
          className={clsx(formStyles['formContainer'], styles['formContainer'])}
        >
          <div
            className={clsx(
              formStyles['formContainer__inner'],
              styles['inner-container'],
            )}
          >
            {networkType && (
              <FormComponent
                multiLogins={multiLogins && bulk_account_creation_allowed}
                touchOnChange
                networkType={networkType}
                networkId={networkId}
                onClose={() =>
                  history.push(
                    pathGen(ROUTES.SERVICES_NETWORK, {
                      networkType,
                    }),
                  )
                }
                asyncValidate={validator(formConfig.schema(networkType))}
              />
            )}
          </div>
        </div>
      </DoublePanelViewLayout>
    </>
  )
}

export default AccountCreate

AccountCreate.propTypes = {
  multiLogins: PropTypes.bool,
}
